import { useCallback } from 'react';

import { useLocation } from 'react-router';

import {
  type TCouponModelData,
  type TGlobalDataModel,
  type TGlobalDataModelPaymentOptions,
  type TGlobalDataModelCheckout,
  type TGlobalDataModelInvestment,
  type TGlobalDataModelAuditLog,
} from '@/_models';
import { useUrlParams, Routes } from '@/_navigation';
import { dateTime, useBoolean } from '@/_shared';
import { setLocalStorageContainer } from '@/_state/actions/generic-container';
import { useAppDispatch } from '@/_state/hooks';

import { useSynchingCache } from './use-synching-cache';

export function useUpdateCache(data: TGlobalDataModel) {
  const { isLocked, checkout } = data;

  const dispatch = useAppDispatch();

  const { termsReviewedByCustomer } = checkout || ({} as TGlobalDataModelCheckout);
  const { pathname } = useLocation();
  const overrideModalCtrl = useBoolean();
  const { paramLeadId, paramSummaryId, paramCampusId, resolvedCurrentPath } = useUrlParams();

  const overrideGlobalData = useCallback(
    (globalData: TGlobalDataModel, forceOverride = false) => {
      if (isLocked && !forceOverride) {
        return;
      }

      dispatch(
        setLocalStorageContainer({
          ...globalData,
          leadId: paramLeadId,
          summaryId: paramSummaryId,
          campusId: paramCampusId,
          timeStamp: dateTime.now(true).format(),
          link: resolvedCurrentPath,
        })
      );
    },
    [dispatch, isLocked, paramCampusId, paramLeadId, paramSummaryId, resolvedCurrentPath]
  );

  const updateGlobalData = useCallback(
    (globalData: TGlobalDataModel, isOverride = false, overrideLock = false) => {
      if (isLocked && !overrideLock) return false;

      if (termsReviewedByCustomer && pathname.indexOf(Routes.CONSENT.link as string) < 0 && !isOverride) {
        overrideModalCtrl[1].setTrue();
        return false;
      }

      overrideGlobalData(
        {
          ...globalData,
          leadId: paramLeadId,
          summaryId: paramSummaryId,
          campusId: paramCampusId,
          timeStamp: dateTime.now(true).format(),
          link: resolvedCurrentPath,
        },
        overrideLock
      );
      return true;
    },
    [
      isLocked,
      termsReviewedByCustomer,
      pathname,
      paramLeadId,
      paramSummaryId,
      paramCampusId,
      resolvedCurrentPath,
      overrideModalCtrl,
      overrideGlobalData,
    ]
  );

  const synchingCache = useSynchingCache(data, updateGlobalData, overrideGlobalData);

  const updateInvestmentData = useCallback(
    (nInvestment: TGlobalDataModelInvestment) =>
      updateGlobalData({ ...data, investment: nInvestment } as TGlobalDataModel),
    [data, updateGlobalData]
  );

  const updatePaymentOptionsData = useCallback(
    (pOptions: TGlobalDataModelPaymentOptions, isOverride = false) => {
      if (pOptions.subscriptionType === 'pif') {
        return updateGlobalData({ ...data, paymentOptions: pOptions }, isOverride);
      }
      return updateGlobalData({ ...data, paymentOptions: pOptions, persistPaymentOptions: pOptions }, isOverride);
    },
    [data, updateGlobalData]
  );

  const updateCheckoutData = useCallback(
    (c: TGlobalDataModelCheckout, isOverride = false) => updateGlobalData({ ...data, checkout: c }, isOverride),
    [data, updateGlobalData]
  );

  const updateCouponsData = useCallback(
    (nCoupons: TCouponModelData) => updateGlobalData({ ...data, coupons: nCoupons } as TGlobalDataModel),
    [data, updateGlobalData]
  );

  const updateAuditLog = useCallback(
    (nAuditLog: TGlobalDataModelAuditLog, isOverride = false) => {
      updateGlobalData({ ...data, auditLog: nAuditLog } as TGlobalDataModel, isOverride);
    },
    [data, updateGlobalData]
  );

  const updateClimb = useCallback(
    (isClimb: boolean) => updateGlobalData({ ...data, is_climb: isClimb } as TGlobalDataModel),
    [data, updateGlobalData]
  );

  return {
    updateClimb,
    updateCouponsData,
    updateCheckoutData,
    updatePaymentOptionsData,
    updateInvestmentData,
    updateGlobalData,
    overrideGlobalData,
    updateAuditLog,
    overrideModalCtrl,
    ...synchingCache,
  };
}

export type TUseUpdateCache = ReturnType<typeof useUpdateCache>;
